import React from 'react';

interface ContractCallAccordionProps {
  expanded: boolean;
  onChange: () => void;
  children: React.ReactNode;
}

/**
 * Lightweight accordion for contract calls (self-custody parity without MUI).
 * Styled components exported separately for flexible composition.
 */
export function ContractCallAccordion({
  expanded,
  onChange,
  children,
}: ContractCallAccordionProps): React.ReactElement {
  return (
    <div className="w-full bg-transparent">
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // Clone element and pass accordion props to child components
          // TypeScript can't infer the exact child type, so we use a safe assertion
          return React.cloneElement(
            child as React.ReactElement<{
              expanded?: boolean;
              onChange?: () => void;
            }>,
            { expanded, onChange }
          );
        }
        return child;
      })}
    </div>
  );
}

interface ContractCallAccordionSummaryProps {
  children: React.ReactNode;
  expanded?: boolean;
  onChange?: () => void;
}

/**
 * Accordion summary (header) with hover state and click handler.
 * Mirrors self-custody ContractCallAccordionSummary behavior.
 */
export function ContractCallAccordionSummary({
  children,
  onChange,
}: ContractCallAccordionSummaryProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onChange}
      className="w-full flex items-center justify-between bg-white border border-zinc-200 rounded-[12px] px-4 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
    >
      <div className="flex-1 min-w-0">{children}</div>
    </button>
  );
}

interface ContractCallAccordionDetailsProps {
  children: React.ReactNode;
  expanded?: boolean;
}

/**
 * Accordion details (body) with border top and background.
 * Mirrors self-custody ContractCallAccordionDetails behavior.
 */
export function ContractCallAccordionDetails({
  children,
  expanded,
}: ContractCallAccordionDetailsProps): React.ReactElement {
  if (!expanded) return <></>;

  return (
    <div className="w-full mt-2 bg-white border border-zinc-200 rounded-[12px] px-4 py-4">
      {children}
    </div>
  );
}
